import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
import { type CallOptions, type Client } from "@connectrpc/connect";
import type { DataType } from "#gen/common/v1/types_pb.js";
import type { Calendar, Event } from "#gen/events/v1/events_pb.js";
import { DeletePolicy, EventService, UpdatePolicy, type EventFilter } from "#gen/events/v1/service_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";

export class EventClient {
	private client: Client<typeof EventService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.events || "/", EventService, options);
	}

	async listCalendars(orgId: string, workspaceId: string, options?: CallOptions): Promise<Calendar[]> {
		try {
			const res = await this.client.listCalendars(
				{
					orgId,
					workspaceId,
				},
				options,
			);
			return res.calendars;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getCalendar(id: string): Promise<Calendar> {
		try {
			const res = await this.client.getCalendar({ id });
			return res.calendar!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createCalendar(calendar: Calendar): Promise<Calendar> {
		try {
			const res = await this.client.createCalendar({
				orgId: calendar.orgId,
				workspaceId: calendar.workspaceId,
				name: calendar.name,
				colorCode: calendar.colorCode,
				isDefault: calendar.isDefault,
			});
			return res.calendar!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateCalendar(calendar: Calendar, paths: string[]): Promise<Calendar> {
		try {
			const mask = create(FieldMaskSchema, { paths });
			const res = await this.client.updateCalendar({ calendar, mask });
			return res.calendar!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteCalendar(id: string): Promise<void> {
		try {
			await this.client.deleteCalendar({ id });
		} catch (e) {
			throw handleError(e);
		}
	}

	async listEvents(orgId: string, workspaceId: string, filter?: EventFilter, options?: CallOptions): Promise<Event[]> {
		try {
			const res = await this.client.listEvents(
				{
					orgId,
					workspaceId,
					filter,
				},
				options,
			);
			return res.events;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEvent(id: string): Promise<Event> {
		try {
			const res = await this.client.getEvent({ id });
			return res.event!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEventProperties(calendarIds: string[]): Promise<Record<string, DataType>> {
		try {
			const res = await this.client.getEventProperties({ calendarIds });
			return res.properties;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createEvent(event: Event): Promise<Event> {
		try {
			const res = await this.client.createEvent({
				orgId: event.orgId,
				workspaceId: event.workspaceId,
				calendarId: event.calendar?.id,
				title: event.title,
				description: event.description,
				allDay: event.allDay,
				startTime: event.startTime,
				endTime: event.endTime,
				timezone: event.timezone,
				recurrenceRule: event.recurrenceRule,
				properties: event.properties,
			});
			return res.event!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateEvent(event: Event, paths: string[], policy: UpdatePolicy = UpdatePolicy.EVENT_ONLY): Promise<Event> {
		try {
			const mask = create(FieldMaskSchema, { paths });
			const res = await this.client.updateEvent({ event, mask, updatePolicy: policy });
			return res.event!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteEvent(id: string, policy: DeletePolicy = DeletePolicy.EVENT_ONLY): Promise<void> {
		try {
			await this.client.deleteEvent({ id, deletePolicy: policy });
		} catch (e) {
			throw handleError(e);
		}
	}
}
